/**
 * Circuit Breaker Pattern Implementation
 * Prevents infinite retry loops and API spam when services are down
 */

let posthog = null;
try {
  posthog = require('posthog-js').default || require('posthog-js');
} catch (_) {}

class CircuitBreaker {
  constructor(options = {}) {
    this.name = options.name || 'default';
    this.failureThreshold = options.failureThreshold || 5;
    this.resetTimeout = options.resetTimeout || 60000;
    this.failureCount = 0;
    this.state = 'CLOSED';
    this.nextAttempt = Date.now();
  }

  async execute(fn, fallback = null) {
    if (this.state === 'OPEN') {
      if (Date.now() < this.nextAttempt) {
        console.warn(`Circuit breaker [${this.name}] is OPEN - request blocked`);
        if (fallback) {
          return fallback();
        }
        throw new Error('Service temporarily unavailable - circuit breaker is open');
      }
      this.state = 'HALF_OPEN';
      this._trackStateChange('HALF_OPEN');
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  onSuccess() {
    if (this.state !== 'CLOSED') {
      this._trackStateChange('CLOSED');
    }
    this.failureCount = 0;
    this.state = 'CLOSED';
  }

  onFailure() {
    this.failureCount++;
    if (this.failureCount >= this.failureThreshold && this.state !== 'OPEN') {
      this.state = 'OPEN';
      this.nextAttempt = Date.now() + this.resetTimeout;
      this._trackStateChange('OPEN');
      console.error(`Circuit breaker [${this.name}] OPEN - too many failures (${this.failureCount})`);
    }
  }

  _trackStateChange(newState) {
    if (posthog) {
      try {
        posthog.capture('circuit_breaker_state_change', {
          circuit_breaker: this.name,
          new_state: newState,
          failure_count: this.failureCount,
          reset_timeout: this.resetTimeout
        });
      } catch (_) {}
    }
  }

  reset() {
    if (this.state !== 'CLOSED') {
      this._trackStateChange('CLOSED');
    }
    this.failureCount = 0;
    this.state = 'CLOSED';
    this.nextAttempt = Date.now();
  }

  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      nextAttempt: this.nextAttempt
    };
  }
}

export const apiCircuitBreaker = new CircuitBreaker({
  name: 'api',
  failureThreshold: 5,
  resetTimeout: 60000
});

export const assetCircuitBreaker = new CircuitBreaker({
  name: 'assets',
  failureThreshold: 3,
  resetTimeout: 30000
});

export const presignedUrlCircuitBreaker = new CircuitBreaker({
  name: 'presignedUrl',
  failureThreshold: 15,
  resetTimeout: 15000
});

export default CircuitBreaker;
